import {
  useEffect,
  type ReactNode,
  type MouseEvent,
  type FocusEvent,
  useCallback,
} from 'react'

import { clsx } from 'clsx'

import type { Uploader } from '@buffer-mono/uploader'

import { ChannelFields } from '~publish/legacy/composer/composer/components/channel-fields'
import { RemindersOmniIndicator } from '~publish/legacy/composer/composer/components/RemindersOmniIndicator'
import { RemindersBar } from '~publish/legacy/reminders'

import AppActionCreators from '../action-creators/AppActionCreators'
import ComposerActionCreators from '../action-creators/ComposerActionCreators'
import { ErrorTypes, NotificationScopes } from '../AppConstants'
import AppStore from '../stores/AppStore'
import { NotificationStore } from '../stores/NotificationStore'
import { AttachmentSwitch } from './attachment-switch/AttachmentSwitch'
import AttachmentGlance from './AttachmentGlance'
import FacebookPostTypeBar from './channel-bar/FacebookPostTypeBar'
import GBPPostTypeBar from './channel-bar/GBPPostTypeBar'
import InstagramPostTypeBar from './channel-bar/InstagramPostTypeBar'
import YoutubePostTypeBar from './channel-bar/YoutubePostTypeBar'
import { ComposerIntegrationsBar } from './ComposerIntegrationsBar'
import ComposerNetworkIcon from './ComposerNetworkIcon'
import { EditorWrapper } from './EditorWrapper'
import LinkAttachment from './LinkAttachment'
import MediaAttachment from './MediaAttachment'
import { BypassValidationWithReminderMessage } from './notices/BypassValidationWithReminderMessage'
import EditorNotices from './notices/EditorNotices'
import { IGVideosAsReelsNotice } from './notices/IGVideosAsReelsNotice'
import MastodonNotice from './notices/MastodonNotice'
import PopcornNotificationContainer from './PopcornNotificationContainer'
import RetweetAttachment from './RetweetAttachment'
import SuggestedMediaBox from './SuggestedMediaBox'
import Thread from './Thread'
import type Draft from '../entities/Draft/Draft'

import styles from './css/Composer.module.css'

type Props = {
  draft: Draft
  forceEditorFocus: boolean
  expandedComposerId: string | null
  children: ReactNode
  uploader: Uploader
  composerRootRef: React.RefObject<HTMLDivElement>
}

function Composer({
  draft,
  forceEditorFocus,
  expandedComposerId,
  children,
  uploader,
  composerRootRef,
}: Props): JSX.Element {
  const isExpanded = expandedComposerId === draft.id
  const otherComposerExpanded =
    expandedComposerId !== draft.id && expandedComposerId !== null
  const shouldAutoFocusEditor = isExpanded || forceEditorFocus

  useEffect(() => {
    if (forceEditorFocus) {
      AppActionCreators.stopForcingEditorFocus()
    }
  }, [forceEditorFocus])

  const onComposerClick = (event: MouseEvent | FocusEvent): void => {
    if (!isExpanded) {
      event.preventDefault()
      expand()
    }
  }

  // Ensure other parts of the app ignore click events coming from inside the composer
  // (e.g. when collapsing an expanded composer when a click is registered outside).
  // Don't prevent default for file input clicks or links, but prevent them from bubbling up,
  // so that the default browser action (file selection) still happens.
  const onUpdateZoneClick = useCallback(
    (event: MouseEvent<HTMLDivElement>): void => {
      if (!isExpanded) {
        return
      }

      const target = event.target

      if (!target || !(target instanceof HTMLElement)) {
        return
      }

      if (target.matches('input[type="file"],input[type="checkbox"],a,label')) {
        event.stopPropagation()
      } else {
        event.preventDefault()
      }
    },
    [isExpanded],
  )

  const expand = useCallback((): void => {
    if (isExpanded) {
      return
    }

    ComposerActionCreators.expand(draft.id)
  }, [isExpanded, draft.id])

  if (!draft.isEnabledOrOmni()) {
    return <div />
  }

  const attachmentGlanceHasNoThumbnail =
    draft.getAttachmentThumbnails() === null
  const visibleNotifications = NotificationStore.getVisibleNotifications()

  const updateZoneClassName = clsx({
    [styles.savedUpdateZone]: draft.isSaved,
    [styles.expandedUpdateZone]: !draft.isSaved && isExpanded,
    [styles.updateZone]: !draft.isSaved && !isExpanded,
    [styles.editableErrorUpdateZone]:
      draft.hasAttachment() && !isExpanded && draft.hasSavingError,
    [styles.lockedDisplayedBehindAnotherZone]: draft.isSaved,
    [styles.newUpdateZone]: isExpanded,
    [styles.displayedBehindAnotherUpdateZone]: !isExpanded,
  })

  const composerClassName = clsx({
    [styles.composerDisplayedBehindAnother]: !isExpanded,
    [styles.composer]: isExpanded,
    [styles.lockedComposer]: draft.isSaved,
    [styles.omnibox]: draft.service.isOmni,
    [styles.newStandardComposer]: !AppStore.getIsOmniboxEnabled() && isExpanded,
  })

  const isImageFirst = draft.isImageFirst() && draft.service.isYoutube()

  const suggestedMediaBoxClassName = clsx({
    [styles.imageFirstSuggestedMediaBox]: isImageFirst,
    [styles.suggestedMediaBoxAlignedBottom]:
      !draft.hasAttachmentSwitch() && !isImageFirst,
  })

  const editorContainerClassName = clsx({
    [styles.imageFirstContainer]: isImageFirst,
    [styles.editorMediaContainer]: !isImageFirst,
  })
  const editorClassName = clsx({
    [styles.imageFirstWrapper]: isImageFirst,
    [styles.editorContainer]: !isImageFirst,
  })
  const shouldShowThreadComponents = draft.canHaveThread() && isExpanded
  const shouldShowEditor = !draft.isStoryPost() && !draft.isFacebookStoryPost()

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      className={composerClassName}
      onClick={onComposerClick}
      onFocus={onComposerClick}
    >
      <ComposerNetworkIcon draft={draft} />

      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div onClick={onUpdateZoneClick} className={updateZoneClassName}>
        <div
          className={clsx(
            styles.composerContentContainer,
            !isExpanded && styles.collapsed,
          )}
        >
          <GBPPostTypeBar draft={draft} />
          <InstagramPostTypeBar draft={draft} />
          <FacebookPostTypeBar draft={draft} />
          <YoutubePostTypeBar draft={draft} />
          {!shouldShowThreadComponents && (
            <>
              <RemindersOmniIndicator draft={draft} />
              <MastodonNotice draft={draft} />
              <IGVideosAsReelsNotice draft={draft} />
              <EditorNotices draft={draft} />
              <div className={editorContainerClassName}>
                <div className={editorClassName}>
                  {shouldShowEditor && (
                    <EditorWrapper
                      draft={draft}
                      shouldAutoFocus={shouldAutoFocusEditor}
                      attachmentGlanceHasNoThumbnail={
                        attachmentGlanceHasNoThumbnail
                      }
                      composerRootRef={composerRootRef}
                    />
                  )}
                  <MediaAttachment draft={draft} uploader={uploader} />
                </div>
                <LinkAttachment draft={draft} />
                <RetweetAttachment draft={draft} />
                {isExpanded && <AttachmentSwitch draft={draft} />}
                <SuggestedMediaBox
                  draft={draft}
                  className={suggestedMediaBoxClassName}
                />
                {isExpanded && draft.service.isInstagram() && (
                  <BypassValidationWithReminderMessage
                    visible={!draft.isReminder}
                    images={draft.images}
                    updateType={draft.updateType}
                  />
                )}
                {isExpanded && (
                  <ComposerIntegrationsBar
                    draft={draft}
                    uploader={uploader}
                    composerRootRef={composerRootRef}
                  />
                )}
                {isExpanded && <RemindersBar draft={draft} />}
              </div>
            </>
          )}
          {shouldShowThreadComponents && (
            <Thread
              editorClassName={editorClassName}
              editorContainerClassName={editorContainerClassName}
              suggestedMediaBoxClassName={suggestedMediaBoxClassName}
              draft={draft}
              shouldAutoFocus={shouldAutoFocusEditor}
              uploader={uploader}
              composerRootRef={composerRootRef}
            />
          )}

          {isExpanded && <ChannelFields draft={draft} />}

          <AttachmentGlance draft={draft} />
        </div>
        {!otherComposerExpanded && (
          <>
            <PopcornNotificationContainer
              visibleNotifications={visibleNotifications}
              scope={`${NotificationScopes.PROFILE_QUEUE_LIMIT}-${draft.service.name}`}
              className={styles.notificationContainer}
            />
            <PopcornNotificationContainer
              visibleNotifications={visibleNotifications}
              scope={`${NotificationScopes.UPDATE_SAVING}-${ErrorTypes.INLINE}-${draft.service.name}`}
            />
          </>
        )}
        {children}
      </div>
    </div>
  )
}

export default Composer
